import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import apiClient from '../services/apiClient';

const CACHE_TTL_MS = 5 * 60 * 1000;
const isCypressTestRun = typeof window !== 'undefined' && Boolean(window.Cypress);

const toNumberOrNull = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return null;
    }
    return numericValue;
};

/**
 * Build cache key for resource optimization payloads.
 *
 * @param {*} projectId
 * @param {*} departmentId
 * @param {string} timeHorizon
 * @param {{ start_date?: string, end_date?: string }} [scopeDates]
 * @returns {string}
 */
export const makeCacheKey = (projectId, departmentId, timeHorizon, scopeDates = null) => {
    const base = `${projectId ?? 'null'}|${departmentId ?? 'null'}|${timeHorizon ?? '3_months'}`;
    if (scopeDates?.start_date && scopeDates?.end_date) {
        return `${base}|${scopeDates.start_date}|${scopeDates.end_date}`;
    }
    return base;
};

/**
 * Derive remaining capacity hours from total capacity and utilization percent.
 *
 * @param {number|null|undefined} totalCapacity - Total capacity hours.
 * @param {number|null|undefined} currentUtilization - Utilization as 0-1 ratio or 0-100 percent.
 * @returns {number|null}
 */
export const deriveAvailableCapacityHours = (totalCapacity, currentUtilization) => {
    const total = Number(totalCapacity);
    const util = Number(currentUtilization);
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(util)) {
        return null;
    }
    const utilPct = util <= 1 ? util * 100 : util;
    return Math.max(0, total - (total * (utilPct / 100)));
};

/**
 * Normalize engine hiring_recommendations (object or array) to dashboard table rows.
 *
 * @param {*} raw - hiring_recommendations from API.
 * @returns {Array<object>}
 */
export const normalizeHiringRecommendationsForUi = (raw) => {
    if (!raw) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    if (typeof raw !== 'object') {
        return [];
    }

    const rows = [];
    const immediate = raw.immediate_needs || {};
    const future = raw.future_needs || {};
    const budgetImpact = raw.budget_impact || {};

    if (Number(immediate.positions_needed) > 0) {
        const skills = Array.isArray(immediate.required_skills) ? immediate.required_skills : [];
        const fteCount = Number(immediate.positions_needed);
        rows.push({
            role: skills[0] || 'Immediate capacity hire',
            urgency: immediate.urgency_level || 'high',
            timeline: 'Immediate',
            skills,
            positions_needed: fteCount,
            justification: `${fteCount} FTE equivalent suggested to close near-term capacity gap (planning estimate, not a named hire)`,
            estimated_cost: toNumberOrNull(budgetImpact.ramp_up_cost) ?? toNumberOrNull(budgetImpact.annual_cost),
            impact_score: immediate.urgency_level === 'high' ? 92 : 78,
        });
    }

    if (Number(future.positions_needed) > 0) {
        const skills = Array.isArray(future.required_skills) ? future.required_skills : [];
        const fteCount = Number(future.positions_needed);
        rows.push({
            role: skills[0] || 'Planned capacity hire',
            urgency: 'medium',
            timeline: future.timeline || '3-6 months',
            skills,
            positions_needed: fteCount,
            justification: `${fteCount} FTE equivalent recommended for upcoming demand`,
            estimated_cost: toNumberOrNull(budgetImpact.annual_cost),
            impact_score: 75,
        });
    }

    return rows;
};

/**
 * Map skills_demand object or array to skills_gap_forecast chart rows.
 *
 * @param {*} skillsDemand
 * @returns {Array<object>}
 */
export const mapSkillsGapForecastForUi = (skillsDemand) => {
    if (!skillsDemand) {
        return [];
    }
    if (Array.isArray(skillsDemand)) {
        return skillsDemand.map((item) => ({
            skill: item.skill || item.name || 'Unknown',
            current_level: toNumberOrNull(item.current_level) ?? 0,
            required_level: toNumberOrNull(item.required_level) ?? 0,
            gap: toNumberOrNull(item.gap) ?? 0,
            growth_rate: toNumberOrNull(item.growth_rate) ?? 1,
        }));
    }
    if (typeof skillsDemand === 'object') {
        return Object.entries(skillsDemand).map(([skill, count]) => {
            const numericCount = Number(count) || 0;
            const required = Math.max(numericCount, 1);
            const gap = Math.max(0, required - numericCount);
            return {
                skill,
                current_level: numericCount,
                required_level: required,
                gap,
                growth_rate: gap > 0 ? gap : numericCount,
            };
        });
    }
    return [];
};

/**
 * Normalize skills_demand from API (dict, list of names, or empty).
 *
 * @param {*} skillsDemand
 * @returns {object}
 */
export const normalizeSkillsDemandInput = (skillsDemand) => {
    if (!skillsDemand) {
        return {};
    }
    if (Array.isArray(skillsDemand)) {
        return skillsDemand.reduce((acc, skill, index) => {
            const name = typeof skill === 'string'
                ? skill
                : (skill?.skill || skill?.name || `Skill ${index + 1}`);
            if (name) {
                acc[name] = (acc[name] || 0) + 1;
            }
            return acc;
        }, {});
    }
    if (typeof skillsDemand === 'object') {
        return skillsDemand;
    }
    return {};
};

/**
 * Build forecast confidence (0-100) from period coverage and utilization gap.
 *
 * @param {number} periodCount
 * @param {number|null} utilizationNeeded
 * @param {string} [riskLevel]
 * @returns {number}
 */
export const computeForecastConfidence = (periodCount, utilizationNeeded, riskLevel = 'medium') => {
    const coverageBoost = Math.min(20, Math.max(0, periodCount - 1) * 4);
    const util = Number(utilizationNeeded);
    let base = 72;
    if (Number.isFinite(util)) {
        if (util > 100) {
            base = 58;
        } else if (util > 85) {
            base = 68;
        } else if (util < 50) {
            base = 82;
        }
    } else if (riskLevel === 'low') {
        base = 85;
    } else if (riskLevel === 'high') {
        base = 62;
    }
    return Math.round(Math.min(95, Math.max(45, base + coverageBoost)));
};

/**
 * Build monthly forecast rows from utilization trend_data.
 *
 * @param {Array<object>} trendData
 * @param {number} fallbackCapacity
 * @param {number|null} utilizationNeeded
 * @param {string} riskLevel
 * @returns {Array<object>}
 */
/**
 * Map utilization trend_data rows into ML forecast historical_data payload shape.
 *
 * @param {Array<object>} trendData
 * @returns {Array<{ period: string, demand_hours: number, project_count: number }>}
 */
export const buildMlForecastHistoricalFromTrend = (trendData) => {
    if (!Array.isArray(trendData) || trendData.length === 0) {
        return [];
    }

    return trendData.map((row, index) => {
        const demand = Number(row?.demand ?? row?.billable_hours ?? row?.actual);
        const demandHours = Number.isFinite(demand) ? demand : 0;
        const projectCountRaw = Number(row?.project_count ?? row?.projects);
        const projectCount = Number.isFinite(projectCountRaw) && projectCountRaw > 0
            ? projectCountRaw
            : 1;

        return {
            period: row?.period || `Period_${index + 1}`,
            demand_hours: demandHours,
            project_count: projectCount,
        };
    });
};

export const buildForecastPeriodsFromTrend = (
    trendData,
    fallbackCapacity,
    utilizationNeeded,
    riskLevel
) => {
    if (!Array.isArray(trendData) || trendData.length === 0) {
        return [];
    }

    return trendData.map((row) => {
        const demand = Number(row.demand ?? row.billable_hours ?? row.actual) || 0;
        const capacity = Number(row.capacity) > 0
            ? Number(row.capacity)
            : fallbackCapacity;
        const gap = Math.max(0, demand - capacity);
        return {
            period: row.period || 'Period',
            demand,
            capacity,
            gap,
            utilization: Number(row.utilization ?? row.actual) || null,
            confidence: computeForecastConfidence(1, utilizationNeeded, riskLevel),
        };
    });
};

/**
 * Aggregate trend rows into quarterly seasonal pattern percentages.
 *
 * @param {Array<object>} trendData
 * @returns {object|null}
 */
export const buildSeasonalPatternsFromTrend = (trendData) => {
    if (!Array.isArray(trendData) || trendData.length === 0) {
        return null;
    }

    const buckets = { Q1: [], Q2: [], Q3: [], Q4: [] };
    trendData.forEach((row) => {
        const period = String(row.period || '');
        const demand = Number(row.demand ?? row.billable_hours ?? row.actual);
        if (!Number.isFinite(demand)) {
            return;
        }
        const monthMatch = period.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/i);
        const monthName = monthMatch ? monthMatch[1].toLowerCase() : '';
        const monthIndex = [
            'jan', 'feb', 'mar', 'apr', 'may', 'jun',
            'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
        ].indexOf(monthName);
        let quarter = 'Q1';
        if (monthIndex >= 3 && monthIndex <= 5) {
            quarter = 'Q2';
        } else if (monthIndex >= 6 && monthIndex <= 8) {
            quarter = 'Q3';
        } else if (monthIndex >= 9) {
            quarter = 'Q4';
        }
        buckets[quarter].push(demand);
    });

    const averages = Object.fromEntries(
        Object.entries(buckets).map(([q, values]) => [
            q,
            values.length ? values.reduce((a, b) => a + b, 0) / values.length : null,
        ])
    );
    const valid = Object.values(averages).filter((v) => Number.isFinite(v));
    if (!valid.length) {
        return null;
    }
    const maxAvg = Math.max(...valid);
    const toPct = (avg) => (
        Number.isFinite(avg) && maxAvg > 0
            ? Math.round((avg / maxAvg) * 100)
            : null
    );

    return {
        q1_demand: toPct(averages.Q1),
        q2_demand: toPct(averages.Q2),
        q3_demand: toPct(averages.Q3),
        q4_demand: toPct(averages.Q4),
    };
};

/**
 * Map nested demand_forecast API payload to dashboard chart shape.
 *
 * @param {object|null|undefined} apiData - Resource optimization API data object.
 * @returns {object|null}
 */
export const mapDemandForecastForUi = (apiData) => {
    const raw = apiData?.demand_forecast || apiData?.demand_forecasting;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const demand = raw.demand_forecast || raw;
    const supply = raw.supply_forecast || {};
    const gap = raw.gap_analysis || {};
    const peak = demand.peak_demand_period || {};
    const peakStart = peak.start_date || peak.start;
    const peakEnd = peak.end_date || peak.end;
    const periodLabel = peakStart && peakEnd
        ? `${peakStart} – ${peakEnd}`
        : 'Current period';
    const totalCapacity = Number(apiData?.totalCapacity);
    const supplyCapacity = Number(supply.available_capacity);
    const fallbackCapacity = Number.isFinite(supplyCapacity) && supplyCapacity > 0
        ? supplyCapacity
        : (Number.isFinite(totalCapacity) ? totalCapacity : 0);

    const utilizationNeeded = Number(gap.utilization_needed);
    const riskLevel = gap.risk_level || 'medium';
    const trendData = Array.isArray(apiData?.trend_data) ? apiData.trend_data : [];

    let forecastPeriods = buildForecastPeriodsFromTrend(
        trendData,
        fallbackCapacity,
        utilizationNeeded,
        riskLevel
    );

    if (!forecastPeriods.length) {
        const totalDemand = Number(demand.total_hours_needed) || 0;
        const periodCapacity = fallbackCapacity;
        forecastPeriods = [{
            period: periodLabel,
            demand: totalDemand,
            capacity: periodCapacity,
            gap: Number(gap.hours_shortage) || Math.max(0, totalDemand - periodCapacity),
            utilization: Number.isFinite(utilizationNeeded) ? utilizationNeeded : null,
            confidence: computeForecastConfidence(1, utilizationNeeded, riskLevel),
        }];
    } else {
        const aggregateConfidence = computeForecastConfidence(
            forecastPeriods.length,
            utilizationNeeded,
            riskLevel
        );
        forecastPeriods = forecastPeriods.map((row) => ({
            ...row,
            confidence: aggregateConfidence,
        }));
    }

    const hiringRaw = raw.hiring_recommendations;
    const budgetImpact = (
        typeof hiringRaw === 'object' && !Array.isArray(hiringRaw)
            ? hiringRaw.budget_impact
            : null
    ) || raw.budget_impact || null;

    const skillsDemandObject = normalizeSkillsDemandInput(demand.skills_demand);

    const financialImpact = budgetImpact ? {
        total_hiring_cost: toNumberOrNull(budgetImpact.annual_cost)
            ?? toNumberOrNull(budgetImpact.ramp_up_cost),
        revenue_at_risk: toNumberOrNull(budgetImpact.revenue_at_risk),
        roi_with_hiring: toNumberOrNull(budgetImpact.roi_with_hiring),
        break_even_months: toNumberOrNull(budgetImpact.break_even_months),
    } : null;

    const seasonalFromApi = raw.seasonal_patterns;
    const seasonalFromTrend = buildSeasonalPatternsFromTrend(trendData);
    const seasonalHasValues = seasonalFromApi && (
        ['q1_demand', 'q2_demand', 'q3_demand', 'q4_demand'].some(
            (key) => toNumberOrNull(seasonalFromApi[key]) !== null
        )
    );

    return {
        forecast_periods: forecastPeriods,
        gap_analysis: gap,
        hiring_recommendations: normalizeHiringRecommendationsForUi(hiringRaw),
        skills_demand: skillsDemandObject,
        skills_gap_forecast: mapSkillsGapForecastForUi(skillsDemandObject),
        financial_impact: financialImpact,
        seasonal_patterns: seasonalHasValues ? seasonalFromApi : seasonalFromTrend,
    };
};

/**
 * Merge utilization-metrics overlay with resource-optimization cache so workload KPIs
 * are not wiped when /utilization/ omits workload_balance or member rows.
 *
 * WORKLOAD_BALANCE NOTE: Prefer metricsData (from /resource-optimization/utilization/)
 * because it alone receives start_date/end_date from DashboardScope timeline and
 * therefore reflects the active global timeline filter. The /resource-optimization/
 * response only varies by time_horizon string and would otherwise pin KPIs to a
 * "now"-relative window regardless of selected timeline.
 *
 * @param {object|null} metricsData - Data from getUtilizationMetrics / setUtilizationData.
 * @param {object|null} cachedData - Data from /resource-optimization/.
 * @returns {object|null}
 */
export const mergeUtilizationDataSlices = (metricsData, cachedData) => {
    if (!metricsData) {
        return cachedData ?? null;
    }
    if (!cachedData) {
        return metricsData;
    }

    const metricsMembers = metricsData.individual_utilizations
        || metricsData.individual_resources
        || [];
    const cachedMembers = cachedData.individual_utilizations
        || cachedData.individual_resources
        || [];
    const mergedMembers = metricsMembers.length ? metricsMembers : cachedMembers;

    return {
        ...cachedData,
        ...metricsData,
        current: {
            ...(cachedData.current || {}),
            ...(metricsData.current || {})
        },
        scenarios: metricsData.scenarios ?? cachedData.scenarios,
        individual_utilizations: mergedMembers,
        individual_resources: mergedMembers,
        workload_balance: metricsData.workload_balance ?? cachedData.workload_balance ?? null,
        demand_forecasting: metricsData.demand_forecasting ?? cachedData.demand_forecasting ?? null,
        trend_data: (metricsData.trend_data?.length ? metricsData.trend_data : cachedData.trend_data) || [],
        recommendations: metricsData.recommendations?.length
            ? metricsData.recommendations
            : (cachedData.recommendations || []),
        capacityGaps: metricsData.capacityGaps ?? cachedData.capacityGaps ?? null,
    };
};

const ResourceOptimizationContext = createContext(null);

export const useResourceOptimizationContext = () => {
    const context = useContext(ResourceOptimizationContext);
    if (!context) {
        throw new Error('useResourceOptimizationContext must be used within ResourceOptimizationProvider');
    }
    return context;
};

export const ResourceOptimizationProvider = ({ children }) => {
    const [dataByKey, setDataByKey] = useState({});
    const [utilizationMetricsData, setUtilizationMetricsData] = useState(null);
    const [loadingByKey, setLoadingByKey] = useState({});
    const [errorByKey, setErrorByKey] = useState({});
    const lastFetchByKey = useRef({});
    const inFlightByKey = useRef({});

    const fetchResourceOptimizationData = useCallback(async (
        projectId,
        departmentId,
        timeHorizon,
        options = {}
    ) => {
        const fullPayload = options.full === true;
        const scopeDates = (options.start_date && options.end_date)
            ? { start_date: options.start_date, end_date: options.end_date }
            : null;
        const key = `${makeCacheKey(projectId, departmentId, timeHorizon, scopeDates)}|${fullPayload ? 'full' : 'lite'}`;
        const now = Date.now();
        if (options.force === true) {
            delete lastFetchByKey.current[key];
        }
        const lastFetch = lastFetchByKey.current[key];
        if (!isCypressTestRun && Number.isFinite(lastFetch) && now - lastFetch < CACHE_TTL_MS) {
            return;
        }

        if (!isCypressTestRun && inFlightByKey.current[key]) {
            return inFlightByKey.current[key];
        }

        setLoadingByKey((prev) => ({ ...prev, [key]: true }));
        setErrorByKey((prev) => ({ ...prev, [key]: null }));

        const requestPromise = (async () => {
            const params = {};
            if (projectId) params.project_id = projectId;
            if (departmentId) params.department_id = departmentId;
            if (timeHorizon) params.time_horizon = timeHorizon;
            if (scopeDates) {
                params.start_date = scopeDates.start_date;
                params.end_date = scopeDates.end_date;
            }
            if (!fullPayload) {
                params.lite = '1';
            }

            const response = await apiClient.get('/resource-optimization/', { params });

            if (response.data.success) {
                const apiData = response.data.data;
                const totalCapacityHours = toNumberOrNull(apiData?.totalCapacity);
                const avgUtilization = toNumberOrNull(apiData?.currentUtilization);
                const availableCapacityHours = toNumberOrNull(apiData?.availableCapacity)
                    ?? deriveAvailableCapacityHours(totalCapacityHours, avgUtilization);
                const resourceDataSet = {
                    availableResources: apiData?.availableResources || [],
                    totalCapacity: totalCapacityHours,
                    currentUtilization: avgUtilization,
                    availableCapacity: availableCapacityHours,
                    overdueProjects: typeof apiData?.overdueProjects === 'number' ? apiData.overdueProjects : null,
                    criticalResources: typeof apiData?.criticalResources === 'number' ? apiData.criticalResources : null,
                    efficiencyScore: apiData?.efficiencyScore ?? null,
                    skillsTracked: typeof apiData?.skillsTracked === 'number' ? apiData.skillsTracked : null,
                    resourceConflicts: apiData?.conflicts ?? [],
                    recommendations: apiData?.recommendations ?? []
                };

                const scenarioRoot = apiData?.utilization_scenarios || {};
                const utilizationDataSet = {
                    current: scenarioRoot?.current || scenarioRoot?.scenarios?.current || {},
                    scenarios: scenarioRoot?.scenarios || {},
                    individual_utilizations: apiData?.individual_utilizations || [],
                    individual_resources: apiData?.individual_utilizations || [],
                    workload_balance: apiData?.workload_balance || null,
                    demand_forecasting: mapDemandForecastForUi(apiData),
                    trend_data: Array.isArray(apiData?.trend_data) ? apiData.trend_data : [],
                    recommendations: scenarioRoot?.recommendations || [],
                    capacityGaps: scenarioRoot?.capacity_gaps || null,
                };

                lastFetchByKey.current[key] = now;
                setDataByKey((prev) => ({
                    ...prev,
                    [key]: { resourceData: resourceDataSet, utilizationData: utilizationDataSet }
                }));
            } else {
                const msg = response.data?.error || 'Resource optimization request failed';
                setErrorByKey((prev) => ({ ...prev, [key]: msg }));
            }
        })();

        inFlightByKey.current[key] = requestPromise;

        try {
            await requestPromise;
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed to load resource optimization data';
            setErrorByKey((prev) => ({ ...prev, [key]: msg }));
        } finally {
            if (inFlightByKey.current[key] === requestPromise) {
                delete inFlightByKey.current[key];
            }
            setLoadingByKey((prev) => ({ ...prev, [key]: false }));
        }
    }, []);

    const setUtilizationData = useCallback((data) => {
        setUtilizationMetricsData(data);
    }, []);

    const clearUtilizationMetricsData = useCallback(() => {
        setUtilizationMetricsData(null);
    }, []);

    const resetForCypress = useCallback(() => {
        if (!isCypressTestRun) {
            return;
        }
        setDataByKey({});
        setUtilizationMetricsData(null);
        setLoadingByKey({});
        setErrorByKey({});
        lastFetchByKey.current = {};
        inFlightByKey.current = {};
    }, []);

    const getDataForKey = useCallback(
        (projectId, departmentId, timeHorizon, options = {}) => {
            const fullPayload = options.full === true;
            const scopeDates = (options.start_date && options.end_date)
                ? { start_date: options.start_date, end_date: options.end_date }
                : null;
            const key = `${makeCacheKey(projectId, departmentId, timeHorizon, scopeDates)}|${fullPayload ? 'full' : 'lite'}`;
            const cached = dataByKey[key];
            return {
                resourceData: cached?.resourceData ?? null,
                utilizationData: mergeUtilizationDataSlices(
                    utilizationMetricsData,
                    cached?.utilizationData ?? null
                ),
                loading: loadingByKey[key] ?? false,
                error: errorByKey[key] ?? null
            };
        },
        [dataByKey, utilizationMetricsData, loadingByKey, errorByKey]
    );

    useEffect(() => {
        if (isCypressTestRun && typeof window !== 'undefined') {
            window.__resetResourceOptimization = resetForCypress;
        }
    }, [resetForCypress]);

    const invalidateResourceOptimizationCache = useCallback((
        projectId,
        departmentId,
        timeHorizon,
        options = {}
    ) => {
        const fullPayload = options.full === true;
        const scopeDates = (options.start_date && options.end_date)
            ? { start_date: options.start_date, end_date: options.end_date }
            : null;
        const key = `${makeCacheKey(projectId, departmentId, timeHorizon, scopeDates)}|${fullPayload ? 'full' : 'lite'}`;
        delete lastFetchByKey.current[key];
        delete inFlightByKey.current[key];
    }, []);

    const value = {
        fetchResourceOptimizationData,
        getDataForKey,
        setUtilizationData,
        clearUtilizationMetricsData,
        invalidateResourceOptimizationCache,
        resetForCypress,
    };

    return (
        <ResourceOptimizationContext.Provider value={value}>
            {children}
        </ResourceOptimizationContext.Provider>
    );
};
